'use client';

import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';

/**
 * Whether a message's text is still typing out on screen. The text is revealed smoothly
 * (`useSmooth`), so it keeps appearing for a moment after its stream part has closed; the activity
 * trail reads this to stay live until the answer has finished appearing, instead of adding rows
 * above an answer that is still typing (which pushes it down mid-sentence).
 */
const RevealingContext = createContext(false);
// Kept apart from the value, so the text parts that report don't re-render when it changes.
const ReportContext = createContext<Dispatch<SetStateAction<number>> | null>(null);

export function TextRevealProvider({ children }: { children: ReactNode }) {
  const [revealingParts, setRevealingParts] = useState(0);
  return (
    <ReportContext.Provider value={setRevealingParts}>
      <RevealingContext.Provider value={revealingParts > 0}>{children}</RevealingContext.Provider>
    </ReportContext.Provider>
  );
}

export function useTextRevealing(): boolean {
  return useContext(RevealingContext);
}

export function useReportTextReveal(revealing: boolean): void {
  const setRevealingParts = useContext(ReportContext);
  // Layout effect: the trail must know before the next paint, or it can paint its closing row
  // above the answer for one frame.
  useLayoutEffect(() => {
    if (!revealing || !setRevealingParts) return;
    setRevealingParts(count => count + 1);
    return () => setRevealingParts(count => count - 1);
  }, [revealing, setRevealingParts]);
}
